import { createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { MonoLabel } from '@/design-system';

/**
 * Waar een tabblad zijn stand en knoppen neerzet, in de donkere kop eromheen.
 *
 * Een keuring heeft één kop met tabbladen (Overzicht, Rapport, Klantpagina). Wat
 * er op het open tabblad speelt - "concept · versie 2" en de knoppen die daarbij
 * horen - staat rechts in die kop, niet in een tweede donkere balk eronder.
 * De kop geeft de twee plekken door; het scherm vult ze met `TabHeader`.
 */
export interface TabHeaderTargets {
    /** Rechts naast de naam van de keuring: de stand en de knoppen. */
    aside: HTMLElement | null;
    /** Over de hele breedte, boven de tabbladen: uitleg en uitkomsten. */
    notes: HTMLElement | null;
}

const TabHeaderTargetsContext = createContext<TabHeaderTargets | null>(null);

export function TabHeaderTargetsProvider({
    targets,
    children,
}: {
    targets: TabHeaderTargets;
    children: ReactNode;
}) {
    return (
        <TabHeaderTargetsContext.Provider value={targets}>
            {children}
        </TabHeaderTargetsContext.Provider>
    );
}

export interface TabHeaderProps {
    /** Wat er op dit tabblad speelt, in mono: "concept · versie 2 · gegenereerd 12 min na opname". */
    status?: string | null;
    /** De knoppen van dit tabblad. Ze staan op de donkere kop: `onDark`, hoogstens één `primary`. */
    actions?: ReactNode;
    /** Een blijvende uitleg of de uitkomst van de laatste actie, over de hele breedte. */
    notes?: ReactNode;
}

/**
 * De stand en de knoppen van een tabblad.
 *
 * Binnen een kop met tabbladen komt dit in die kop terecht. Zonder zo'n kop -
 * in een test, of een scherm dat los wordt getoond - staat het als eigen donkere
 * balk bovenaan, zodat er nooit een knop verdwijnt.
 *
 * De knoppen gaan zonder omhulsel mee: rendert een knop niets (zoals "Opname
 * heropenen" als dat niet kan), dan blijft de plek leeg en neemt hij geen ruimte.
 * De plek is daarom een rij die afbreekt; de stand vult de eerste regel.
 */
export function TabHeader({ status, actions, notes }: TabHeaderProps) {
    const targets = useContext(TabHeaderTargetsContext);
    const aside = (
        <>
            {status ? (
                <MonoLabel
                    size={10}
                    tone="onDarkMuted"
                    as="p"
                    className="m-0 basis-full tracking-[0.1em]"
                >
                    {status}
                </MonoLabel>
            ) : null}
            {actions}
        </>
    );

    if (!targets) {
        return (
            <header className="flex shrink-0 flex-wrap items-center gap-2 bg-[var(--ink-800)] px-6 py-4 empty:hidden">
                {aside}
                {notes ? <div className="flex basis-full flex-col gap-2">{notes}</div> : null}
            </header>
        );
    }

    return (
        <>
            {targets.aside ? createPortal(aside, targets.aside) : null}
            {notes && targets.notes ? createPortal(notes, targets.notes) : null}
        </>
    );
}
